import { useCallback, useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { formatCOP, formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { downloadXlsx } from '@/lib/download';
import { toast } from 'sonner';
import { FileDown, Receipt, DollarSign, TrendingUp, CheckCircle2 } from 'lucide-react';
import { Pagination, usePagination } from '@/components/Pagination';

const WD_STATUS = {
  pending: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved: { label: 'Aprobado', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  paid: { label: 'Pagado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rechazado', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

export default function AdminWithdrawals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [active, setActive] = useState(null); // withdrawal to process
  const [action, setAction] = useState('approved');
  const [notes, setNotes] = useState('');
  const [finance, setFinance] = useState(null);
  const { pageItems, page, setPage, totalPages, total } = usePagination(items);

  useSEO({
    title: 'Finanzas | Administración',
    description: 'Consulta ingresos, comisiones, saldos disponibles y gestiona los retiros de vendedores.',
    canonical: '/admin/finanzas',
  });

  const refresh = useCallback(() => Promise.all([
    api.get('/admin/withdrawals', { params: status !== 'all' ? { status } : {} }).then(({ data }) => setItems(data.items || [])),
    api.get('/finance/summary').then(({ data }) => setFinance(data)).catch(() => {}),
  ]), [status]);
  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    try {
      await api.put(`/admin/withdrawals/${active.withdrawal_id}`, { status: action, notes });
      toast.success(`Retiro marcado: ${WD_STATUS[action].label}`);
      setActive(null); setNotes(''); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Finanzas</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="export-withdrawals-btn"
              onClick={() => downloadXlsx('/admin/exports/withdrawals', 'retiros.xlsx')}
              variant="outline" className="rounded-none"
            >
              <FileDown className="mr-2 h-4 w-4" /> Exportar retiros
            </Button>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-48 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="approved">Aprobados</SelectItem>
                <SelectItem value="paid">Pagados</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Fase D — Métricas globales */}
        {finance && (
          <div data-testid="admin-finance-metrics" className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <FinKpi icon={Receipt} label="Ventas plataforma" value={finance.total_sales} testid="admin-fin-sales" />
            <FinKpi icon={DollarSign} label="Bruto vendido" value={formatCOP(finance.gross_amount)} testid="admin-fin-gross" />
            <FinKpi icon={TrendingUp} label="Comisiones cobradas" value={formatCOP(finance.commissions)} testid="admin-fin-commissions" />
            <FinKpi icon={CheckCircle2} label="Desembolsado" value={formatCOP(finance.disbursed)} testid="admin-fin-disbursed" />
          </div>
        )}

        <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Vendedor</th>
                <th className="px-4 py-3">Monto</th>
                <th className="px-4 py-3">Datos bancarios</th>
                <th className="px-4 py-3">Solicitado</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Sin solicitudes</td></tr>}
              {pageItems.map((w) => {
                const st = WD_STATUS[w.status] || { label: w.status, cls: 'bg-slate-100' };
                return (
                  <tr key={w.withdrawal_id} className="row-hover align-top">
                    <td className="px-4 py-3 font-mono text-xs">{w.withdrawal_id}</td>
                    <td className="px-4 py-3">{w.seller_name}</td>
                    <td className="px-4 py-3 font-bold text-slate-900">{formatCOP(w.amount)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-pre-wrap max-w-xs">{w.bank_info}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(w.requested_at)}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-none border ${st.cls}`}>{st.label}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {w.status !== 'paid' && w.status !== 'rejected' && (
                        <Button
                          data-testid={`process-${w.withdrawal_id}`}
                          size="sm" className="rounded-none bg-slate-900 hover:bg-slate-800"
                          onClick={() => { setActive(w); setAction(w.status === 'pending' ? 'approved' : 'paid'); }}
                        >
                          Procesar
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} testIdPrefix="admin-withdrawals-pag" />

        <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle>Procesar retiro · {active && formatCOP(active.amount)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Aprobar</SelectItem>
                  <SelectItem value="paid">Marcar pagado</SelectItem>
                  <SelectItem value="rejected">Rechazar</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas internas (opcional)"
                rows={3}
                className="rounded-none"
              />
              <Button onClick={submit} className="w-full rounded-none bg-slate-900 hover:bg-slate-800">
                Confirmar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

const FinKpi = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-500" />
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    </div>
    <div className="mt-2 font-display text-xl font-black text-slate-900">{value}</div>
  </div>
);
